/**
 * Quick filter utilities for MUI DataGrid — normalizes heterogeneous cell values
 * (string, number, Date, plain object, array) to a searchable string.
 */

import { formatDateToDateString } from 'shared/lib/dateFormatter';

const MAX_DEPTH = 8;

/** Keys processed first so trade-partner-style `{ name }` rows match labels before other fields */
const QUICK_FILTER_PREFERRED_KEYS = ['name', 'title', 'label', 'message', 'code', 'value'];

/**
 * Flattens a cell value into a single space-separated string for substring search.
 *
 * @param {*} value - Raw cell value (from row[field] after valueGetter)
 * @param {number} [depth=0]
 * @param {WeakSet<object>} [seen]
 * @returns {string}
 */
export function flattenCellValueForQuickFilter(value, depth = 0, seen = new WeakSet()) {
    if (depth > MAX_DEPTH) {
        return '';
    }
    if (value === null || value === undefined) {
        return '';
    }

    const t = typeof value;
    if (t === 'string') {
        return value;
    }
    if (t === 'number' || t === 'boolean' || t === 'bigint') {
        return String(value);
    }
    if (t === 'symbol' || t === 'function') {
        return '';
    }

    if (value instanceof Date) {
        const time = value.getTime();
        if (Number.isNaN(time)) {
            return '';
        }
        // Index the calendar day the cell shows. The UTC spelling was dropped: for a viewer
        // east of UTC it names the previous day, so searching yesterday's date matched today's rows.
        return [
            formatDateToDateString(value),
            value.toLocaleDateString('en-US'),
            value.toLocaleDateString('en-US', { year: '2-digit', month: '2-digit', day: '2-digit' }),
        ].join(' ');
    }

    if (Array.isArray(value)) {
        return value
            .map((v) => flattenCellValueForQuickFilter(v, depth + 1, seen))
            .filter(Boolean)
            .join(' ');
    }

    if (t === 'object') {
        if (seen.has(value)) {
            return '';
        }
        seen.add(value);
        const ownKeys = Object.keys(value);
        const orderedKeys = [
            ...QUICK_FILTER_PREFERRED_KEYS.filter((k) => ownKeys.includes(k)),
            ...ownKeys.filter((k) => !QUICK_FILTER_PREFERRED_KEYS.includes(k)),
        ];
        return orderedKeys
            .map((k) => flattenCellValueForQuickFilter(value[k], depth + 1, seen))
            .filter(Boolean)
            .join(' ');
    }

    return String(value);
}

/**
 * DataGrid `sortComparator` for cells whose value may be an array/object (MUI's default
 * comparator only handles number/string/Date meaningfully). Falls back to numeric/locale
 * compare for primitives, and flattens array/object values before comparing.
 *
 * @param {*} v1
 * @param {*} v2
 * @returns {number}
 */
export function complexValueSortComparator(v1, v2) {
    const isComplex = (v) => v !== null && typeof v === 'object' && !(v instanceof Date);
    if (!isComplex(v1) && !isComplex(v2)) {
        if (typeof v1 === 'number' && typeof v2 === 'number') return v1 - v2;
        return String(v1 ?? '').localeCompare(String(v2 ?? ''), undefined, { numeric: true, sensitivity: 'base' });
    }
    return flattenCellValueForQuickFilter(v1).localeCompare(flattenCellValueForQuickFilter(v2), undefined, {
        numeric: true,
        sensitivity: 'base',
    });
}

/**
 * "Empty" check for the DataGrid `isEmpty`/`isNotEmpty` filter operators that also understands
 * array-valued cells (MUI's default only checks `value == null || value === ''`, so an empty
 * array — the common case for these array-shaped columns — is never treated as empty).
 *
 * @param {*} value
 * @returns {boolean}
 */
export function isEmptyComplexValue(value) {
    if (value == null || value === '') return true;
    if (Array.isArray(value)) return value.every((item) => item == null || String(item).trim() === '');
    return false;
}

/**
 * @param {string} filterValue - Toolbar quick filter token
 * @returns {function} (cellValue) => boolean — MUI `getApplyQuickFilterFn`
 */
export const createQuickFilterFn = (filterValue) => {
    const raw = filterValue == null ? '' : String(filterValue).trim();
    if (!raw) {
        return () => true;
    }
    const needle = raw.toLowerCase();

    return (value) => {
        const haystack = flattenCellValueForQuickFilter(value).toLowerCase();
        return haystack.includes(needle);
    };
};

/**
 * Date cells: match user input against MM/DD/YY style display.
 * @returns {function} (cellValue) => boolean
 */
export const createQuickFilterFn_MMDDYY = (filterValue) => {
    return (cellValue) => {
        if (!cellValue || filterValue == null || String(filterValue).trim() === '') {
            return false;
        }
        const value = String(filterValue);

        const formatToMMDDYY = (dateStr) => {
            const date = new Date(dateStr);
            if (Number.isNaN(date.getTime())) {
                return String(dateStr);
            }

            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const year = String(date.getFullYear()).slice(-2);
            return `${month}/${day}/${year}`;
        };

        const formattedFilter = formatToMMDDYY(value);
        const formattedCell = formatToMMDDYY(cellValue);

        return String(formattedCell).toLowerCase().includes(String(formattedFilter).toLowerCase());
    };
};
